import React from "react";

/**
 * Renders one `application/ld+json` block.
 *
 * The site shipped with **zero** structured data, so Google had nothing to
 * work with beyond the raw HTML: no publisher identity, no article bylines or
 * dates, no breadcrumb trail, no FAQ eligibility. For a site whose rankable
 * assets are a handful of articles and an FAQ accordion, that is the cheapest
 * signal available and it was simply absent.
 *
 * Server component on purpose — this must be in the SSR HTML. Googlebot does
 * render JS, but structured data found in the initial response is processed in
 * the first indexing pass rather than the deferred rendering one.
 *
 * `dangerouslySetInnerHTML` is required: React escapes `<`, `>` and `&` inside
 * a text child, which corrupts the JSON. `<` is escaped to `<` here
 * instead — that is valid JSON, and it is what stops a CMS string containing
 * `</script>` from breaking out of the block.
 *
 * NOTE for the deferred CSP (documentation/security-headers.md): these are
 * inline `<script>` elements. Chrome applies `script-src` to them even though
 * they never execute, so a future nonce- or hash-based policy has to account
 * for every one of these blocks, not just the two executable inline scripts
 * that doc currently tracks.
 */
export function JsonLd({ data }: { data: Record<string, unknown> }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(data).replace(/</g, "\\u003c"),
      }}
    />
  );
}

export default JsonLd;
